import { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import type {
  Notification as PushNotification,
  NotificationActionPerformedEvent,
} from '@capacitor-firebase/messaging';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { UsePushNotificationsReturn } from '../hooks/usePushNotifications';
import { useAuth } from './AuthProvider';
import { franchiseApi } from '../services/franchiseApi';
import { emitNotificationsChanged, isSafeRoute } from '../services/notificationEvents';
import { pushPlatform } from '../services/pushTokens';
import { captureEvent } from '../services/posthog';

type PushNotificationContextValue = UsePushNotificationsReturn;

/**
 * Read a notification's `data` payload as string fields. FCM only ever delivers string values in
 * `data`, but the plugin types it `unknown`, so this is where that assumption is checked rather
 * than asserted.
 */
function asStringRecord(value: unknown): Record<string, string | undefined> | null {
  if (typeof value !== 'object' || value === null) return null;
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

const PushNotificationContext = createContext<PushNotificationContextValue | undefined>(
  undefined
);

export function usePushNotificationContext() {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error(
      'usePushNotificationContext must be used within PushNotificationProvider'
    );
  }
  return context;
}

interface PushNotificationProviderProps {
  children: ReactNode;
  /**
   * Auto-register for push notifications on mount if permission granted
   */
  autoRegister?: boolean;
}

export function PushNotificationProvider({
  children,
  autoRegister = false,
}: PushNotificationProviderProps) {
  const { user, isAuthenticated } = useAuth();

  // The tap handler is a dependency of the hook's listener effect, and useNavigate returns a new
  // function on every route change; read through a ref so navigating doesn't tear down and re-add
  // the FCM listeners (and re-run auto-registration) each time.
  const navigate = useNavigate();
  const navigateRef = useRef<NavigateFunction>(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Handle notification received while app is in foreground
  const handleNotificationReceived = useCallback(
    (notification: PushNotification) => {
      console.log('[PushNotificationProvider] Notification received:', notification);

      // Track in PostHog
      captureEvent('push_notification_received', {
        title: notification.title,
        has_data: !!notification.data,
        notification_id: notification.id,
      });

      // A push means a new notification row, so the bell's count is already stale.
      emitNotificationsChanged();
    },
    []
  );

  // Handle notification tapped (app opened from notification)
  const handleNotificationTapped = useCallback((action: NotificationActionPerformedEvent) => {
    console.log('[PushNotificationProvider] Notification tapped:', action);

    const notification = action.notification;

    // Track in PostHog
    captureEvent('push_notification_tapped', {
      title: notification.title,
      action_id: action.actionId,
      notification_id: notification.id,
      data: notification.data,
    });

    // FCM types the payload as `unknown` (the old APNs plugin used `any`), so narrow before
    // reading it — a malformed `data` from the sending side must not throw in a tap handler.
    const data = asStringRecord(notification.data);
    const notificationId = data?.notificationId;
    const route = data?.route;
    const url = data?.url;

    // Opening the push is reading it. Best effort: a cold start can deliver the tap before the
    // session is restored, and the inbox still offers the notification to be read there.
    if (notificationId) {
      franchiseApi
        .markNotificationRead(notificationId)
        .catch(error => {
          console.warn('[PushNotificationProvider] Could not mark notification read:', error);
        })
        .finally(emitNotificationsChanged);
    }

    // Handle deep linking based on notification data
    if (isSafeRoute(route)) {
      // In-app navigation rather than a full page load, which would restart the app.
      console.log('[PushNotificationProvider] Navigating to route:', route);
      navigateRef.current(route);
    } else if (url) {
      // Open external URL
      console.log('[PushNotificationProvider] Opening URL:', url);
      window.open(url, '_blank');
    }
  }, []);

  const pushNotifications = usePushNotifications(
    handleNotificationReceived,
    handleNotificationTapped,
    autoRegister
  );
  const { token, unregister: deleteDeviceToken } = pushNotifications;

  // The user and token pair last sent to the backend, so each token is registered once per
  // signed-in user per session rather than on every render or remount of the consumer tree.
  const registeredRef = useRef<string | null>(null);

  // Send token to backend once there is both a token and a session to attach it to
  useEffect(() => {
    const platform = pushPlatform();
    if (!token || !isAuthenticated || !platform) return;

    const key = `${user?.email ?? ''}\n${token}`;
    if (registeredRef.current === key) return;
    registeredRef.current = key;

    // Errors are swallowed: a device that can't register (an outage, or a 403 while an admin is
    // viewing as someone else) simply gets no pushes, and everything else works without them.
    franchiseApi.registerPushToken(token, platform).then(
      () => {
        console.log('[PushNotificationProvider] Token sent to backend successfully');
        captureEvent('push_token_registered', { success: true });
      },
      error => {
        console.error('[PushNotificationProvider] Error sending token to backend:', error);
        captureEvent('push_token_registered', {
          success: false,
          error: String(error),
        });
        // Forget it, so the next sign-in tries this token again.
        if (registeredRef.current === key) registeredRef.current = null;
      }
    );
  }, [token, isAuthenticated, user?.email]);

  // Turn push off on this device: the backend first, while the token is still known, then FCM.
  const unregister = useCallback(async () => {
    const platform = pushPlatform();
    if (token && platform) {
      try {
        await franchiseApi.unregisterPushToken(token, platform);
      } catch (error) {
        // Deleting the FCM token below still stops delivery; the backend prunes the dead token
        // the next time a send to it fails.
        console.warn('[PushNotificationProvider] Could not unregister token on the backend:', error);
      }
    }
    registeredRef.current = null;
    await deleteDeviceToken();
  }, [token, deleteDeviceToken]);

  const value: PushNotificationContextValue = {
    ...pushNotifications,
    unregister,
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
}
